/**
 * FirmAssist Analytics Service
 * Unified tracking layer: Firebase Analytics + Microsoft Clarity
 * 
 * Firebase Analytics: funnels, retention, user properties, feature usage
 * Microsoft Clarity: session replays, heatmaps, dead clicks, rage clicks
 */

let analytics = null;
let analyticsReady = false;

// ── INITIALIZE ──
export const initAnalytics = async (app) => {
  try {
    const { getAnalytics, setAnalyticsCollectionEnabled } = await import('firebase/analytics');
    analytics = getAnalytics(app);
    setAnalyticsCollectionEnabled(analytics, true);
    analyticsReady = true;
    console.log('[Analytics] Firebase Analytics initialized');
  } catch (e) {
    console.warn('[Analytics] Firebase Analytics failed to init:', e.message);
  }
};

// ── CORE: Log event to Firebase Analytics ──
const logEvent = (eventName, params = {}) => {
  if (!analyticsReady || !analytics) return;
  try {
    import('firebase/analytics').then(({ logEvent: firebaseLogEvent }) => {
      // Firebase event names: max 40 chars, alphanumeric + underscore
      const clean = eventName.substring(0, 40);
      // Firebase params: max 100 params, keys max 40 chars, string values max 100 chars
      const cleanParams = {};
      Object.entries(params).forEach(([k, v]) => {
        const key = k.substring(0, 40);
        if (typeof v === 'string') cleanParams[key] = v.substring(0, 100);
        else if (typeof v === 'number' || typeof v === 'boolean') cleanParams[key] = v;
      });
      firebaseLogEvent(analytics, clean, cleanParams);
    });
  } catch (e) {
    // Silent fail — analytics should never break the app
  }

  // Also tag in Clarity for session-level filtering
  try {
    if (window.clarity) {
      window.clarity('event', eventName);
    }
  } catch (e) {}
};

// ── USER IDENTITY ──
export const identifyUser = (userId, userProps = {}) => {
  if (!analyticsReady || !analytics) return;
  try {
    import('firebase/analytics').then(({ setUserId, setUserProperties }) => {
      setUserId(analytics, userId);
      setUserProperties(analytics, {
        firm_id: userProps.firmId || '',
        user_role: userProps.role || '',
        plan_tier: userProps.plan || 'solo',
        firm_name: (userProps.firmName || '').substring(0, 36),
      });
    });
  } catch (e) {}

  // Clarity custom identity
  try {
    if (window.clarity) {
      window.clarity('identify', userId);
      window.clarity('set', 'firmId', userProps.firmId || '');
      window.clarity('set', 'role', userProps.role || '');
      window.clarity('set', 'plan', userProps.plan || 'solo');
    }
  } catch (e) {}
};

// ══════════════════════════════════════════════════════════════════
// EVENT CATEGORIES — call these from App.js, Login.js, etc.
// ══════════════════════════════════════════════════════════════════

// ── AUTH EVENTS ──
export const trackSignUp = (method = 'email') => {
  logEvent('sign_up', { method });
};

export const trackLogin = (method = 'email') => {
  logEvent('login', { method });
};

export const trackLoginFailed = (errorCode, isMobile = false) => {
  logEvent('login_failed', { error_code: errorCode, is_mobile: isMobile });
};

export const trackLogout = () => {
  logEvent('logout');
};

// ── FUNNEL EVENTS (visit → signup → first case → first task → first draft) ──
export const trackFunnelStep = (step) => {
  // Steps: 'visit_login', 'signup_started', 'signup_completed', 
  //        'first_case', 'first_task', 'first_draft', 'first_review',
  //        'first_message_sent', 'first_invoice'
  logEvent('funnel_step', { step });
};

// ── NAVIGATION ──
export const trackTabView = (tabName) => {
  logEvent('tab_view', { tab: tabName });
};

// ── CASE MANAGEMENT ──
export const trackCaseCreated = (caseType) => {
  logEvent('case_created', { case_type: caseType || 'unknown' });
};

export const trackCaseClosed = () => {
  logEvent('case_closed');
};

export const trackCaseDeleted = () => {
  logEvent('case_deleted');
};

// ── TASK MANAGEMENT ──
export const trackTaskCreated = (eventType = 'task') => {
  logEvent('task_created', { event_type: eventType });
};

export const trackTaskCompleted = (eventType = 'task') => {
  logEvent('task_completed', { event_type: eventType });
};

export const trackCourtDateCreated = () => {
  logEvent('court_date_created');
};

// ── CLIENT MANAGEMENT ──
export const trackClientCreated = () => {
  logEvent('client_created');
};

// ── PARALEGAL AI ──
export const trackParalegalMessage = (intent) => {
  logEvent('paralegal_message', { intent: intent || 'unknown' });
};

export const trackParalegalDraft = (docType) => {
  logEvent('paralegal_draft', { doc_type: docType || 'unknown' });
};

// ── DOCUMENT INTELLIGENCE ──
export const trackDocumentDraft = (docType) => {
  logEvent('document_drafted', { doc_type: docType || 'unknown' });
};

export const trackDocumentReview = () => {
  logEvent('document_reviewed');
};

export const trackDocumentCompare = () => {
  logEvent('documents_compared');
};

export const trackCourtFormFilled = (formType) => {
  logEvent('court_form_filled', { form_type: formType || 'unknown' });
};

export const trackPrecedentSearch = (query) => {
  logEvent('precedent_searched', { query: (query || '').substring(0, 50) });
};

// ── BILLING ──
export const trackTimeLogged = (hours) => {
  logEvent('time_logged', { hours: Math.round(hours * 10) / 10 });
};

export const trackInvoiceCreated = (amount) => {
  logEvent('invoice_created', { amount: Math.round(amount) });
};

export const trackRetainerCreated = () => {
  logEvent('retainer_created');
};

// ── COMMUNICATION ──
export const trackMessageSent = (channel) => {
  // channel: 'email', 'sms', 'whatsapp'
  logEvent('message_sent', { channel });
};

export const trackCourtReminderSent = (channel) => {
  logEvent('court_reminder_sent', { channel });
};

// ── PLAYBOOKS ──
export const trackPlaybookAssigned = (playbookName) => {
  logEvent('playbook_assigned', { playbook: (playbookName || '').substring(0, 40) });
};

export const trackPlaybookStepCompleted = (playbookName) => {
  logEvent('playbook_step_completed', { playbook: (playbookName || '').substring(0, 40) });
};

// ── CALENDAR ──
export const trackGoogleCalendarSync = () => {
  logEvent('gcal_synced');
};

// ── EXPORT ──
export const trackDocumentExported = (format) => {
  logEvent('document_exported', { format: format || 'unknown' });
};

// ── QUIZ ──
export const trackQuizStarted = (category) => {
  logEvent('quiz_started', { category: (category || '').substring(0, 40) });
};

export const trackQuizCompleted = (score, total) => {
  logEvent('quiz_completed', { score, total, percentage: Math.round((score / total) * 100) });
};

// ── SUBSCRIPTION ──
export const trackPlanUpgrade = (fromPlan, toPlan) => {
  logEvent('plan_upgrade', { from: fromPlan, to: toPlan });
};

export const trackPaymentAttempt = (plan, amount) => {
  logEvent('payment_attempt', { plan, amount: Math.round(amount) });
};

export const trackPaymentSuccess = (plan, amount) => {
  logEvent('payment_success', { plan, amount: Math.round(amount) });
};

// ── ERROR TRACKING ──
export const trackError = (category, message) => {
  logEvent('app_error', { 
    category: (category || '').substring(0, 40), 
    message: (message || '').substring(0, 100) 
  });
};

// ── SESSION METRICS ──
export const trackSessionStart = (platform) => {
  logEvent('session_start_custom', {
    platform: platform || 'unknown',
    screen_width: window.innerWidth,
    screen_height: window.innerHeight,
    is_mobile: window.innerWidth < 768,
    is_pwa: window.matchMedia('(display-mode: standalone)').matches,
    user_agent_short: navigator.userAgent.substring(0, 100),
  });
};

const AnalyticsService = {
  initAnalytics,
  identifyUser,
  trackSignUp,
  trackLogin,
  trackLoginFailed,
  trackLogout,
  trackFunnelStep,
  trackTabView,
  trackCaseCreated,
  trackCaseClosed,
  trackCaseDeleted,
  trackTaskCreated,
  trackTaskCompleted,
  trackCourtDateCreated,
  trackClientCreated,
  trackParalegalMessage,
  trackParalegalDraft,
  trackDocumentDraft,
  trackDocumentReview,
  trackDocumentCompare,
  trackCourtFormFilled,
  trackPrecedentSearch,
  trackTimeLogged,
  trackInvoiceCreated,
  trackRetainerCreated,
  trackMessageSent,
  trackCourtReminderSent,
  trackPlaybookAssigned,
  trackPlaybookStepCompleted,
  trackGoogleCalendarSync,
  trackDocumentExported,
  trackQuizStarted,
  trackQuizCompleted,
  trackPlanUpgrade,
  trackPaymentAttempt,
  trackPaymentSuccess,
  trackError,
  trackSessionStart,
};
export default AnalyticsService;
